import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileSpreadsheet, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { StockData } from '../types';
import { parseExcelDate, parseExcelTime } from '../utils/dateHelpers';

interface FileUploadProps {
  onDataUpload: (data: StockData[]) => void;
  onError: (error: string) => void;
  isLoading: boolean;
}


const parseDatesInStockData = (data: any[]): StockData[] => {
  return data.map(row => {
    const mainDate = parseExcelDate(row.Date);
    if (!mainDate) return null;

    return {
      ...row,
      Date: mainDate,
      'Hod Time': parseExcelTime(row['Hod Time']),
      'Lod Time': parseExcelTime(row['Lod Time']),
      'Pre Market High Time': parseExcelTime(row['Pre Market High Time']),
      'Pre Market Low Time': parseExcelTime(row['Pre Market Low Time']),
    };
  }).filter(row => row !== null);
};

export const FileUpload: React.FC<FileUploadProps> = ({ onDataUpload, onError, isLoading }) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

        if (jsonData.length === 0) {
          onError('Il file Excel è vuoto o non contiene dati validi');
          return;
        }

        // Valida colonne richieste
        const requiredColumns = ['Date', 'Ticker', 'Open', 'High', 'Low', 'Close', 'Volume'];
        const firstRow = jsonData[0];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));
        
        if (missingColumns.length > 0) {
          onError(`Colonne richieste mancanti: ${missingColumns.join(', ')}`);
          return;
        }

        // Parsa le date e converte i dati con gestione sicura
        const parsedData = parseDatesInStockData(jsonData);
        
        // Valida che ci siano dati validi
        const validData = parsedData.filter(row => 
          row && // Assicurati che la riga non sia null
          row.Date instanceof Date && 
          !isNaN(row.Date.getTime()) &&
          row.Ticker &&
          typeof row.Open === 'number' &&
          typeof row.Close === 'number'
        );

        if (validData.length === 0) {
          onError('Nessun dato valido trovato nel file. Controlla il formato dei dati e assicurati che le date siano valide.');
          return;
        }

        if (validData.length < parsedData.length) {
          const skippedRows = parsedData.length - validData.length;
          console.warn(`${skippedRows} righe con dati non validi sono state ignorate`);
        }

        console.log(`Dati caricati con successo: ${validData.length} righe valide su ${jsonData.length} totali`);
        onDataUpload(validData);
      } catch (error) {
        console.error('Errore nel parsing del file:', error);
        onError('Impossibile analizzare il file Excel. Controlla il formato del file e assicurati che le date siano in un formato valido.');
      }
    };
    reader.readAsBinaryString(file);
  }, [onDataUpload, onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    disabled: isLoading
  });

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-6 sm:p-12 text-center cursor-pointer transition-all duration-200
          ${isDragActive 
            ? 'border-blue-400 bg-blue-400/10' 
            : 'border-gray-600 hover:border-gray-500 bg-gray-800/50 hover:bg-gray-800/80'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        
        <div className="flex flex-col items-center space-y-3 sm:space-y-4">
          {isLoading ? (
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-400"></div>
          ) : (
            <div className={`p-2 sm:p-3 rounded-full ${isDragActive ? 'bg-blue-400/20' : 'bg-gray-700'}`}>
              {isDragActive ? (
                <FileSpreadsheet className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
              ) : (
                <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
              )}
            </div>
          )}
          
          <div>
            <p className="text-base sm:text-lg font-medium text-white mb-2">
              {isLoading ? 'Elaborazione file...' : 'Carica File Excel'}
            </p>
            <p className="text-xs sm:text-sm text-gray-400">
              {isDragActive 
                ? 'Rilascia il file qui...' 
                : 'Trascina e rilascia il tuo file Excel qui, o clicca per selezionare'
              }
            </p>
          </div>
          
          <div className="text-xs text-gray-500">
            Formati supportati: .xlsx, .xls
          </div>
        </div>
      </div>
      
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs sm:text-sm font-medium text-white mb-2">Formato Excel Previsto:</p>
            <div className="text-xs text-gray-400 space-y-1">
              <p>• Colonne richieste: Date, Ticker, Open, High, Low, Close, Volume</p>
              <p>• Dati pre-market: Pre Market High/Low/Volume, Gap %</p>
              <p>• Dati intraday: 1/5 Min High, 5 Min Low, volumi orari</p>
              <p>• Dati temporali: Hod Time, Lod Time, Pre Market High/Low Time (opzionali)</p>
              <p>• Dati aziendali: Market Cap, Shares Outstanding</p>
              <p>• <strong>Nota:</strong> Le date non valide verranno automaticamente ignorate</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};